import { Popover } from 'components'
import { CheckIcon, Cog6ToothIcon, LeaveIcon, LinkIcon } from 'components/icons'
import { RefObject, useContext, useEffect, useRef } from 'react'
import { trackEvent, useDisclosure } from 'utils'
import { useFeedbackQRCode } from '../utils/appData'
import { useScale } from '../utils/scaleUtils'
import { glassPanelStyle } from './GlassPanel'
import { ExplorerConfigContext, SessionContext, ShortcutContext, TimeoutContext } from './providers'
import { SButton, SButtonProps, SIcon, SImage, SText, SView, SViewProps } from './scaled'

const feedbackTimeout = 30 * 1000

export default function FooterButtons() {
  const { status } = useContext(SessionContext)
  const ref = useRef<HTMLDivElement>(null)

  if (status === 'init' || status === 'screensaver') return null

  const commonProps: SViewProps = {
    bottom: 40,
    direction: 'row',
    gap: 15,
    position: 'absolute',
    zIndex: 2,
  }

  return (
    <>
      <SView ref={ref} {...commonProps} left={40}>
        <SettingsButton />
        <UserFeedback parentRef={ref} />
      </SView>

      <SView {...commonProps} right={40}>
        <ShortcutButton />
        <EndSessionButton />
      </SView>
    </>
  )
}

function SettingsButton(props: SButtonProps) {
  const { preview, endSession } = useContext(SessionContext)
  if (preview) return null

  return (
    <SButton LeftIcon={Cog6ToothIcon} onClick={() => endSession('settings', 'init')} {...props} />
  )
}

function UserFeedback({ parentRef }: { parentRef: RefObject<HTMLDivElement | null> }) {
  const scale = useScale()
  const { status, routeSteps: formRoute } = useContext(SessionContext)
  const { event } = useContext(ExplorerConfigContext)
  const qrCodeMedia = useFeedbackQRCode()
  const { open, onOpen, onClose } = useDisclosure()

  useEffect(() => {
    if (open) onClose()
  }, [status, formRoute])

  useEffect(() => {
    if (!open) return

    const close = setTimeout(() => {
      onClose()
    }, feedbackTimeout)

    return () => clearTimeout(close)
  }, [open])

  if (!qrCodeMedia || Boolean(event)) return null

  const feedbackWidth = 300
  const qrCodeDimension = feedbackWidth - 100

  return (
    <Popover
      open={open}
      onOpen={() => {
        trackEvent('Feedback Opened', { status })
        onOpen()
      }}
      onClose={onClose}
      hideClose
      closeOnInteractOutside
      //@ts-ignore
      contentProps={glassPanelStyle(scale)}
      portal={{ container: parentRef }}
      trigger={<SButton LeftIcon={(p: any) => <SIcon value="chat" {...p} />} />}
      triggerProps={{ asChild: true }}
    >
      <SView p={30} gap={20} align="center" w={feedbackWidth}>
        <SText textAlign="center" fontStyle="italic" fontSize={20}>
          Scan with your device to provide feedback.
        </SText>
        <SImage borderRadius={40} src={qrCodeMedia.url} h={qrCodeDimension} w={qrCodeDimension} />
      </SView>
    </Popover>
  )
}

function ShortcutButton(props: SButtonProps) {
  const { templateDocs } = useContext(ExplorerConfigContext)
  const { showShortcut, toggleShortcut } = useContext(ShortcutContext)
  const disableShortcut = !templateDocs?.template.layoutShortcut

  if (disableShortcut) return null

  return (
    <SButton onClick={toggleShortcut} LeftIcon={showShortcut ? CheckIcon : LinkIcon} {...props}>
      {showShortcut ? 'Done' : undefined}
    </SButton>
  )
}

function EndSessionButton(props: SButtonProps) {
  const { isIdle } = useContext(TimeoutContext)
  const { endSession, routeSteps } = useContext(SessionContext)
  const { theme } = useContext(ExplorerConfigContext)

  const firstStep = routeSteps.length === 1

  return (
    <SButton
      background={isIdle && !firstStep ? theme.color.primary : undefined}
      color={isIdle && !firstStep ? 'black' : undefined}
      LeftIcon={LeaveIcon}
      onClick={() => endSession('early-button')}
      title=""
      {...props}
    >
      End Session
    </SButton>
  )
}
